import type { EconomyTransaction } from '@puzzlemania/economy';
// Type-only import — erased at build time, so it never affects which provider's SDK
// code actually ships (see the dynamic import() inside resolveSupabaseServices below,
// which is what does the real code-splitting: WP-WEB.6).
import type { SupabaseAppConfig } from '@puzzlemania/providers-supabase';
import { DEV_DEFAULTS, MockPurchaseProvider } from '@puzzlemania/providers-mock';
import type { Logger } from '@puzzlemania/shared';
import { createPlatformServices, type PlatformServices } from './createPlatformServices';

function readSupabaseConfig(): SupabaseAppConfig {
  const env = import.meta.env;
  const url = env.VITE_SUPABASE_URL;
  const anonKey = env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) {
    throw new Error(
      'resolvePlatformServices: VITE_BAAS_PROVIDER=supabase requires VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to be set.',
    );
  }
  return { url, anonKey };
}

/**
 * RevenueCat is orthogonal to the BaaS choice above — it's gated on *platform*, not
 * backend. Native Android uses `purchases-capacitor` (raw Play Billing product ids);
 * web (the main site only — never native, and never reached by the portal build, which
 * has no Shop UI at all) uses `purchases-js` (RevenueCat Web Billing, Stripe-backed).
 * Falls back to Mock whenever the relevant API key isn't configured. Both real
 * providers are dynamically imported, matching the Supabase code-splitting pattern
 * above, so neither SDK ships in a bundle that will never use it.
 */
async function resolvePurchaseProvider(
  overrides: Partial<PlatformServices>,
): Promise<PlatformServices['purchase']> {
  if (overrides.purchase) return overrides.purchase;

  const { Capacitor } = await import('@capacitor/core');
  const isNativeAndroid = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';

  const androidApiKey = import.meta.env.VITE_REVENUECAT_ANDROID_API_KEY;
  if (androidApiKey && isNativeAndroid) {
    const { RevenueCatPurchaseProvider } = await import('@puzzlemania/providers-revenuecat');
    return new RevenueCatPurchaseProvider({ apiKey: androidApiKey });
  }

  const webApiKey = import.meta.env.VITE_REVENUECAT_WEB_API_KEY;
  if (webApiKey && !isNativeAndroid) {
    const { RevenueCatWebPurchaseProvider } = await import('@puzzlemania/providers-revenuecat-web');
    return new RevenueCatWebPurchaseProvider({ apiKey: webApiKey });
  }

  return new MockPurchaseProvider();
}

export interface ResolvedPlatformServices {
  providers: PlatformServices;
  /**
   * Set only for the real-backend (non-DEV) path. Supabase's Postgres RLS blocks direct
   * client writes to saves/{uid}/modules/economy, so the economy module's
   * wallet-persistence subscription must not attempt one there — this hook (a real
   * server-side validated call) is the only legitimate write path for that module.
   */
  onEconomyTransaction?: (tx: EconomyTransaction) => void;
}

function buildOnEconomyTransaction(
  submit: (request: {
    kind: EconomyTransaction['kind'];
    currencyType: EconomyTransaction['currencyType'];
    amount: number;
    id: string;
    nonce: string;
  }) => void,
): (tx: EconomyTransaction) => void {
  return (tx) => {
    submit({
      kind: tx.kind,
      currencyType: tx.currencyType,
      amount: tx.amount,
      id: tx.id,
      nonce: tx.nonce,
    });
  };
}

async function resolveSupabaseServices(
  overrides: Partial<PlatformServices>,
  logger?: Logger,
): Promise<ResolvedPlatformServices> {
  const {
    SupabaseAnalyticsProvider,
    SupabaseAuthProvider,
    SupabaseDataProvider,
    SupabaseEconomyValidator,
    SupabaseRemoteConfigProvider,
  } = await import('@puzzlemania/providers-supabase');

  const config = readSupabaseConfig();

  const providers: PlatformServices = {
    auth: overrides.auth ?? new SupabaseAuthProvider(config),
    data: overrides.data ?? new SupabaseDataProvider(config),
    remoteConfig: overrides.remoteConfig ?? new SupabaseRemoteConfigProvider(config, DEV_DEFAULTS),
    analytics: overrides.analytics ?? new SupabaseAnalyticsProvider(config),
    purchase: await resolvePurchaseProvider(overrides),
  };

  const validator = new SupabaseEconomyValidator(config, logger);
  return { providers, onEconomyTransaction: buildOnEconomyTransaction((r) => validator.submit(r)) };
}

/**
 * Resolves the real platform providers for this app load: Mock* in DEV (unchanged),
 * real Supabase-backed providers otherwise (see Module 27 — Firebase, previously a peer
 * BaaS implementation behind these same interfaces, was removed entirely; Supabase is
 * now the sole real backend, no runtime switch needed). Async because constructing the
 * real providers can require awaiting.
 */
export async function resolvePlatformServices(
  overrides: Partial<PlatformServices> = {},
  logger?: Logger,
): Promise<ResolvedPlatformServices> {
  if (import.meta.env.DEV) {
    return { providers: createPlatformServices(overrides) };
  }

  return resolveSupabaseServices(overrides, logger);
}
